import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Avatar,
  Chip,
  IconButton,
  Stack,
} from '@mui/material';
import {
  Person as PersonIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Lock as LockIcon,
  Email as EmailIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { sileo } from 'sileo';
import { useNavigate } from 'react-router-dom';
import { useApiInterceptor } from '../utils/apiInterceptor';
import { LoadingOrb } from '../components/LoadingOrb';

const ProfilePage: React.FC = () => {
  const { user, updateUser, token } = useAuth();
  const navigate = useNavigate();
  const apiFetch = useApiInterceptor();
  
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  
  // Campos editables
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    professionalId: '',
    specialty: '',
  });

  // Campos de contraseña
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });

  // Campos de email
  const [emailData, setEmailData] = useState({
    newEmail: '',
    password: '',
  });

  // Inicializar datos del formulario
  useEffect(() => {
    if (user) {
      setFormData({
        firstName: user.firstName || '',
        lastName: user.lastName || '',
        phone: user.phone || '',
        professionalId: user.professionalId || '',
        specialty: user.specialty || '',
      });
    }
  }, [user]);

  const handleInputChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePasswordChange = (field: string, value: string) => {
    setPasswordData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleEmailChange = (field: string, value: string) => {
    setEmailData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveProfile = async () => {
    try {
      setIsLoading(true);

      const response = await apiFetch(`http://localhost:5001/api/auth/profile`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Error actualizando perfil');
      }

      const data = await response.json();
      
      // Actualizar contexto local
      updateUser(data.user);
      
      sileo.success({
        title: 'Perfil actualizado',
        description: 'Tus datos se guardaron correctamente.',
      });
      setIsEditing(false);

    } catch (error) {
      sileo.error({
        title: 'No se pudo actualizar el perfil',
        description: error instanceof Error ? error.message : 'Ocurrió un error inesperado. Intenta nuevamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      sileo.error({
        title: 'Las contraseñas no coinciden',
        description: 'Verifica que la nueva contraseña y su confirmación sean iguales.',
      });
      return;
    }

    try {
      setIsLoading(true);

      const response = await apiFetch(`http://localhost:5001/api/auth/change-password`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Error cambiando contraseña');
      }

      sileo.success({
        title: 'Contraseña actualizada',
        description: 'Tu contraseña se cambió correctamente.',
      });
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });

    } catch (error) {
      sileo.error({
        title: 'No se pudo cambiar la contraseña',
        description: error instanceof Error ? error.message : 'Ocurrió un error inesperado. Intenta nuevamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangeEmail = async () => {
    try {
      setIsLoading(true);

      const response = await apiFetch(`http://localhost:5001/api/auth/change-email`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          newEmail: emailData.newEmail,
          password: emailData.password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Error cambiando email');
      }

      const data = await response.json();
      
      // Actualizar contexto local
      updateUser(data.user);
      
      sileo.success({
        title: 'Email actualizado',
        description: 'Tu correo se cambió correctamente.',
      });
      setEmailData({ newEmail: '', password: '' });

    } catch (error) {
      sileo.error({
        title: 'No se pudo cambiar el email',
        description: error instanceof Error ? error.message : 'Ocurrió un error inesperado. Intenta nuevamente.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const getRoleColor = (role: string) => {
    switch (role) {
      case 'admin':
        return 'error';
      case 'evaluator':
        return 'primary';
      case 'viewer':
        return 'default';
      default:
        return 'default';
    }
  };

  const getRoleLabel = (role: string) => {
    switch (role) {
      case 'admin':
        return 'Administrador';
      case 'evaluator':
        return 'Evaluador';
      case 'viewer':
        return 'Visualizador';
      default:
        return role;
    }
  };

  if (!user) {
    return <LoadingOrb size={64} minHeight="400px" />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, flex: 1 }}>
        {/* Portada del perfil */}
        <Card sx={{ mb: 3, overflow: 'visible' }}>
          <Box
            sx={{
              height: 100,
              borderRadius: '16px 16px 0 0',
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
            }}
          />
          <CardContent sx={{ pt: 0, mt: -6 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ xs: 'center', sm: 'flex-end' }}>
              <Avatar
                sx={{
                  width: 96,
                  height: 96,
                  bgcolor: 'secondary.main',
                  fontSize: '2.25rem',
                  fontWeight: 600,
                  border: (theme) => `4px solid ${theme.palette.background.paper}`,
                }}
              >
                {user.firstName?.charAt(0) || 'U'}
              </Avatar>

              <Box sx={{ flex: 1, textAlign: { xs: 'center', sm: 'left' }, pb: { sm: 0.5 } }}>
                <Typography variant="h5" component="h1" sx={{ fontWeight: 700 }}>
                  {user.firstName} {user.lastName}
                </Typography>
                <Stack
                  direction="row"
                  spacing={1.5}
                  alignItems="center"
                  justifyContent={{ xs: 'center', sm: 'flex-start' }}
                  sx={{ mt: 0.5 }}
                >
                  <Chip label={getRoleLabel(user.role)} color={getRoleColor(user.role)} size="small" />
                  <Typography variant="body2" color="text.secondary">
                    {user.specialty || 'Sin especialidad'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    · Miembro desde {new Date(user.createdAt).toLocaleDateString()}
                  </Typography>
                </Stack>
              </Box>
            </Stack>
          </CardContent>
        </Card>

        {/* Información personal + sistema, en 2 columnas para aprovechar el ancho */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '2fr 1fr' },
            gap: 3,
            mb: 3,
            alignItems: 'stretch',
          }}
        >
          {/* Información del perfil */}
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h6" component="h2">
                  Información Personal
                </Typography>

                {!isEditing ? (
                  <Button
                    startIcon={<EditIcon />}
                    onClick={() => setIsEditing(true)}
                    variant="outlined"
                  >
                    Editar
                  </Button>
                ) : (
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button
                      startIcon={<SaveIcon />}
                      onClick={handleSaveProfile}
                      variant="contained"
                      disabled={isLoading}
                    >
                      {isLoading ? <LoadingOrb size={20} /> : 'Guardar'}
                    </Button>
                    <Button
                      startIcon={<CancelIcon />}
                      onClick={() => {
                        setIsEditing(false);
                        // Restaurar datos originales
                        setFormData({
                          firstName: user.firstName || '',
                          lastName: user.lastName || '',
                          phone: user.phone || '',
                          professionalId: user.professionalId || '',
                          specialty: user.specialty || '',
                        });
                      }}
                      variant="outlined"
                    >
                      Cancelar
                    </Button>
                  </Box>
                )}
              </Box>

              <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 3 }}>
                <TextField
                  fullWidth
                  label="Nombre"
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />

                <TextField
                  fullWidth
                  label="Apellido"
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  disabled={!isEditing}
                  InputProps={{
                    startAdornment: <PersonIcon sx={{ mr: 1, color: 'text.secondary' }} />,
                  }}
                />

                <TextField
                  fullWidth
                  label="Teléfono"
                  value={formData.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                  disabled={!isEditing}
                />

                <TextField
                  fullWidth
                  label="ID Profesional"
                  value={formData.professionalId}
                  onChange={(e) => handleInputChange('professionalId', e.target.value)}
                  disabled={!isEditing}
                />

                <TextField
                  fullWidth
                  label="Especialidad"
                  value={formData.specialty}
                  onChange={(e) => handleInputChange('specialty', e.target.value)}
                  disabled={!isEditing}
                  sx={{ gridColumn: '1 / -1' }}
                />
              </Box>
            </CardContent>
          </Card>

          {/* Información del sistema */}
          <Card sx={{ height: '100%' }}>
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ mb: 2.5 }}>
                Información del Sistema
              </Typography>

              <Stack spacing={2} divider={<Box sx={{ borderTop: 1, borderColor: 'divider' }} />}>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email</Typography>
                  <Typography variant="body2">{user.email}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Estado</Typography>
                  <Typography variant="body2">{user.isActive ? 'Activo' : 'Inactivo'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Email Verificado</Typography>
                  <Typography variant="body2">{user.emailVerified ? 'Sí' : 'No'}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">Último Login</Typography>
                  <Typography variant="body2">
                    {user.lastLogin ? new Date(user.lastLogin).toLocaleString() : 'N/A'}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Box>

        {/* Seguridad: contraseña y email, en 2 columnas */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' },
            gap: 3,
            alignItems: 'start',
          }}
        >
          {/* Cambio de contraseña */}
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <LockIcon sx={{ mr: 1, color: 'primary.main' }} />
                Cambiar Contraseña
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2.5 }}>
                <TextField
                  fullWidth
                  label="Contraseña Actual"
                  type="password"
                  value={passwordData.currentPassword}
                  onChange={(e) => handlePasswordChange('currentPassword', e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Nueva Contraseña"
                  type="password"
                  value={passwordData.newPassword}
                  onChange={(e) => handlePasswordChange('newPassword', e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Confirmar Nueva Contraseña"
                  type="password"
                  value={passwordData.confirmPassword}
                  onChange={(e) => handlePasswordChange('confirmPassword', e.target.value)}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleChangePassword}
                  disabled={isLoading || !passwordData.currentPassword || !passwordData.newPassword || !passwordData.confirmPassword}
                  startIcon={isLoading ? <LoadingOrb size={20} /> : <LockIcon />}
                >
                  Cambiar Contraseña
                </Button>
              </Box>
            </CardContent>
          </Card>

          {/* Cambio de email */}
          <Card>
            <CardContent>
              <Typography variant="h6" component="h2" sx={{ mb: 3, display: 'flex', alignItems: 'center' }}>
                <EmailIcon sx={{ mr: 1, color: 'primary.main' }} />
                Cambiar Email
              </Typography>

              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2.5 }}>
                <TextField
                  fullWidth
                  label="Nuevo Email"
                  type="email"
                  value={emailData.newEmail}
                  onChange={(e) => handleEmailChange('newEmail', e.target.value)}
                />

                <TextField
                  fullWidth
                  label="Contraseña Actual (para confirmar)"
                  type="password"
                  value={emailData.password}
                  onChange={(e) => handleEmailChange('password', e.target.value)}
                />

                <Button
                  fullWidth
                  variant="contained"
                  onClick={handleChangeEmail}
                  disabled={isLoading || !emailData.newEmail || !emailData.password}
                  startIcon={isLoading ? <LoadingOrb size={20} /> : <EmailIcon />}
                >
                  Cambiar Email
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Container>
    </Box>
  );
};

export default ProfilePage; 